import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useState } from "react";
import { Link } from "react-router-dom";
import { redirectToFlow360Login } from "../flow360/api";
import { authClient } from "../lib/auth";

export default function SignIn() {
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  // Include the base path (e.g. "/thread") for subpath deployments.
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const callbackUrl = `${window.location.origin}${base}/auth/callback`;

  const currentAppPath = () => {
    const fullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (base && fullPath.startsWith(`${base}/`)) {
      return fullPath.slice(base.length);
    }
    if (base && fullPath === base) {
      return "/";
    }
    return fullPath || "/";
  };

  const currentReturnPath = () => {
    const path = currentAppPath();
    if (path === "/sign-in" || path.startsWith("/auth/callback")) {
      return undefined;
    }
    return path;
  };

  const rememberPostAuthDestination = () => {
    const path = currentAppPath();
    // Never store the sign-in or callback paths themselves — the authed
    // routes block has no /sign-in route, and /auth/callback re-enters
    // this same flow. Either would render blank after sign-in.
    if (path === "/sign-in" || path.startsWith("/auth/callback")) {
      sessionStorage.removeItem("thread.post_auth_redirect");
      return;
    }
    sessionStorage.setItem("thread.post_auth_redirect", path);
  };

  const handleGoogle = async () => {
    setError(null);
    setSigningIn(true);
    rememberPostAuthDestination();
    const { error: signInError } = await authClient.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: callbackUrl },
    });
    if (signInError) {
      setError(signInError.message);
      setSigningIn(false);
    }
  };

  const handleFlow360 = () => {
    rememberPostAuthDestination();
    redirectToFlow360Login(currentReturnPath());
  };

  return (
    <Flex
      justify="center"
      align="center"
      style={{ minHeight: "100vh", background: "var(--color-background)" }}
    >
      <Card size="4" style={{ width: 380, padding: 32 }}>
        <Flex direction="column" gap="4" align="center">
          <Heading size="6">Sign in to Thread</Heading>
          <Text size="2" color="gray" align="center">
            Provenance tracking for computational workflows
          </Text>

          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Button
              size="3"
              variant="surface"
              onClick={handleGoogle}
              disabled={signingIn}
              style={{ width: "100%", cursor: "pointer" }}
            >
              {signingIn ? "Connecting..." : "Continue with Google"}
            </Button>
            <Card
              variant="surface"
              style={{
                background: "var(--amber-a2)",
                borderColor: "var(--amber-a6)",
              }}
            >
              <Text
                as="p"
                size="1"
                color="amber"
                style={{ lineHeight: 1.45, margin: 0 }}
              >
                Flow360 sign-in exchanges and stores your Flow360 API key in
                Thread so project runs can be submitted and monitored on your
                behalf.
              </Text>
            </Card>
            <Button
              size="3"
              variant="surface"
              color="orange"
              onClick={handleFlow360}
              style={{ width: "100%", cursor: "pointer" }}
            >
              Continue with Flow360
            </Button>
          </Flex>
          {error && (
            <Text size="2" color="red" align="center" style={{ lineHeight: 1.45 }}>
              {error}
            </Text>
          )}
          <Text
            size="1"
            color="gray"
            align="center"
            style={{ lineHeight: 1.45 }}
          >
            Access to Thread is logged. We record page views, timestamps,
            sign-in identity when available, and basic technical metadata for
            security, support, and engagement analytics. See the{" "}
            <Link to="/privacy" style={{ color: "var(--accent-11)" }}>
              Thread privacy notice
            </Link>
            .
          </Text>
        </Flex>
      </Card>
    </Flex>
  );
}
